import React from 'react';
import Button, {
  ButtonType
} from 'syngenta-digital-cropwise-react-ui-kit/dist/components/button';
import Notification from 'syngenta-digital-cropwise-react-ui-kit/dist/components/notification';
import {
  NOTIFICATION_CONFIG,
  NOTIFICATION_TYPES,
  NotificationType
} from './notificationConstants';

export interface NotificationOptions {
  onDuplicate?: () => void;
  onPrint?: () => void;
  onClose?: () => void;
  onViewWorkOrder?: () => void;
  onRetry?: () => void;
}

export interface NotificationStrings {
  SAVED: string;
  UPDATED: string;
  DELETED: string;
  DUPLICATE_BUTTON: string;
  PRINT_PDF_BUTTON: string;
  ERROR_TITLE: string;
  ERROR_MESSAGE: string;
  DELETE_ERROR_TITLE: string;
  DELETE_ERROR_MESSAGE: string;
  CONTACT_BUTTON: string;
  VIEW_WORK_ORDER_BUTTON?: string;
  PDF_ERROR_TITLE?: string;
  PDF_ERROR_MESSAGE?: string;
  PDF_TRY_AGAIN_BUTTON?: string;
}

export const closeNotification = (classNamePrefix: string) => {
  const notificationElement = document.querySelector(
    `.${classNamePrefix}-notification`
  );
  if (notificationElement) {
    const closeButton = notificationElement.querySelector(
      '.syt-antd-notification-notice-close'
    );
    if (closeButton) {
      (closeButton as HTMLElement).click();
    }
  }
};

const focusButtonRef = (element: HTMLDivElement | null) => {
  if (element) {
    const button = element.querySelector('button');
    button?.focus();
  }
};

const handleDuplicate = (
  classNamePrefix: string,
  options?: NotificationOptions
) => {
  closeNotification(classNamePrefix);
  options?.onDuplicate?.();
};

const handlePrint = (
  classNamePrefix: string,
  options?: NotificationOptions
) => {
  closeNotification(classNamePrefix);
  options?.onPrint?.();
};

// Declared before createGenericNotification so CREATE_SUCCESS can check it
let pendingModalCallback: (() => void) | null = null;
let pendingModalClassNamePrefix: string | null = null;

const handleRetry = (
  classNamePrefix: string,
  options?: NotificationOptions
) => {
  closeNotification(classNamePrefix);
  options?.onRetry?.();
};

export const createGenericNotification = (
  type: NotificationType,
  strings: NotificationStrings,
  notificationKey: string,
  classNamePrefix: string,
  options?: NotificationOptions
) => {
  const commonProps = {
    className: `${classNamePrefix}-notification`,
    top: NOTIFICATION_CONFIG.TOP_OFFSET,
    duration: NOTIFICATION_CONFIG.DURATION,
    onClose: options?.onClose || (() => {})
  };

  const contactSupportOnClick = () => {
    window.open(
      'https://www.syngentadigital.com/us-en#support',
      '_blank',
      'noopener'
    );
  };

  const supportButton = (
    <Button
      onClick={contactSupportOnClick}
      type={ButtonType.primary}
      ghost
      style={{
        borderColor: '#14803C',
        color: '#14803C',
        backgroundColor: 'transparent'
      }}
    >
      {strings.CONTACT_BUTTON}
    </Button>
  );

  // PDF_EXPORT_ERROR renders Contact Support alongside a primary Try Again
  // action, so Contact Support is de-emphasized as an outline button to
  // keep a single primary call-to-action.
  const supportButtonOutline = (
    <Button
      onClick={contactSupportOnClick}
      type={ButtonType.outline}
      style={{
        borderColor: '#14803C',
        color: '#14803C',
        backgroundColor: 'transparent'
      }}
    >
      {strings.CONTACT_BUTTON}
    </Button>
  );

  switch (type) {
    case NOTIFICATION_TYPES.CREATE_SUCCESS: {
      Notification.success({
        ...commonProps,
        key: notificationKey,
        // Hold notification open when a modal is about to be shown on top of it,
        // but only for the specific notification type that registered the callback.
        duration:
          pendingModalCallback !== null &&
          pendingModalClassNamePrefix === classNamePrefix
            ? 0
            : commonProps.duration,
        // @ts-ignore
        message: <span className="font-semibold">{strings.SAVED}</span>,
        style: { width: '277px' },
        description: (
          <div className="flex justify-end gap-2" style={{ marginTop: '16px' }}>
            <div className="flex-none" ref={focusButtonRef}>
              <Button
                type={ButtonType.outline}
                onClick={() => handleDuplicate(classNamePrefix, options)}
              >
                {strings.DUPLICATE_BUTTON}
              </Button>
            </div>
            <div className="flex-none">
              <Button
                type={ButtonType.primary}
                onClick={() => handlePrint(classNamePrefix, options)}
              >
                {strings.PRINT_PDF_BUTTON}
              </Button>
            </div>
          </div>
        )
      });
      break;
    }

    case NOTIFICATION_TYPES.EDIT_SUCCESS:
      Notification.success({
        ...commonProps,
        // @ts-ignore
        message: <span className="font-semibold">{strings.UPDATED}</span>,
        duration: NOTIFICATION_CONFIG.SUCCESS_DURATION
      });
      break;

    case NOTIFICATION_TYPES.DELETE_SUCCESS:
      Notification.success({
        ...commonProps,
        // @ts-ignore
        message: <span className="font-semibold">{strings.DELETED}</span>,
        duration: NOTIFICATION_CONFIG.SUCCESS_DURATION
      });
      break;

    case NOTIFICATION_TYPES.DELETE_ERROR:
      Notification.error({
        ...commonProps,
        // @ts-ignore
        message: (
          <span className="font-semibold">{strings.DELETE_ERROR_TITLE}</span>
        ),
        description: (
          <div className="flex flex-col">
            <div
              className="flex"
              style={{ marginTop: '8px', marginBottom: '8px' }}
            >
              <span className="font-normal">
                {strings.DELETE_ERROR_MESSAGE}
              </span>
            </div>
            <div className="flex justify-end">
              <div className="flex-none">{supportButton}</div>
            </div>
          </div>
        )
      });
      break;

    case NOTIFICATION_TYPES.CREATE_OR_EDIT_ERROR:
      Notification.error({
        ...commonProps,
        // @ts-ignore
        message: <span className="font-semibold">{strings.ERROR_TITLE}</span>,
        description: (
          <div className="flex flex-col">
            <div
              className="flex"
              style={{ marginTop: '8px', marginBottom: '8px' }}
            >
              <span className="font-normal">{strings.ERROR_MESSAGE}</span>
            </div>
            <div className="flex justify-end">
              <div className="flex-none">{supportButton}</div>
            </div>
          </div>
        )
      });
      break;

    case NOTIFICATION_TYPES.CONVERSION_DELETE_ERROR:
      Notification.error({
        ...commonProps,
        // @ts-ignore
        message: (
          <span className="font-semibold">{strings.DELETE_ERROR_TITLE}</span>
        ),
        description: (
          <div className="flex flex-col">
            <div
              className="flex"
              style={{ marginTop: '8px', marginBottom: '8px' }}
            >
              <span className="font-normal">
                {strings.DELETE_ERROR_MESSAGE}
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <div className="flex-none">{supportButton}</div>
              <div className="flex-none">
                <Button
                  type={ButtonType.primary}
                  color="green"
                  className="bg-green-60"
                  style={{
                    backgroundColor: '#14803C',
                    borderColor: '#14803C'
                  }}
                  onClick={() => options?.onViewWorkOrder?.()}
                >
                  {strings.VIEW_WORK_ORDER_BUTTON || 'View Work Order'}
                </Button>
              </div>
            </div>
          </div>
        )
      });
      break;

    case NOTIFICATION_TYPES.PDF_EXPORT_ERROR:
      Notification.error({
        ...commonProps,
        // @ts-ignore
        message: (
          <span className="font-semibold">{strings.PDF_ERROR_TITLE}</span>
        ),
        description: (
          <div className="flex flex-col">
            <div
              className="flex"
              style={{ marginTop: '8px', marginBottom: '8px' }}
            >
              <span className="font-normal">{strings.PDF_ERROR_MESSAGE}</span>
            </div>
            <div className="flex justify-end gap-2">
              <div className="flex-none">{supportButtonOutline}</div>
              {options?.onRetry && (
                <div className="flex-none">
                  <Button
                    type={ButtonType.primary}
                    color="green"
                    className="bg-green-60"
                    style={{
                      backgroundColor: '#14803C',
                      borderColor: '#14803C'
                    }}
                    onClick={() => handleRetry(classNamePrefix, options)}
                  >
                    {strings.PDF_TRY_AGAIN_BUTTON}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )
      });
      break;

    default:
      break;
  }
};

// Global state to track if print event listener is ready
let isPrintListenerReady = false;
let pendingNotification: (() => void) | null = null;
let pendingNotificationClassNamePrefix: string | null = null;
// A print dispatch requested before the print listener (ActionMenu) mounted.
// Flushed once setPrintListenerReady(true) is called.
let pendingPrintDispatch: (() => void) | null = null;

export const registerPendingModalCallback = (
  cb: (() => void) | null,
  classNamePrefix?: string
): void => {
  pendingModalCallback = cb;
  pendingModalClassNamePrefix = cb === null ? null : classNamePrefix ?? null;
};

// Fire any deferred success banner and modal. The banner is fired first so it
// sees a non-null pendingModalCallback and stays open (duration: 0) under the
// modal. Callable on page mount so the modal + banner no longer wait for the
// operations list to finish loading.
export const flushPendingOperationNotifications = (): void => {
  if (pendingNotification) {
    const notification = pendingNotification;
    pendingNotification = null;
    pendingNotificationClassNamePrefix = null;
    notification();
  }

  if (pendingModalCallback) {
    const cb = pendingModalCallback;
    pendingModalCallback = null;
    cb();
  }
};

export const cancelPendingNotification = (classNamePrefix: string): void => {
  if (pendingNotificationClassNamePrefix === classNamePrefix) {
    pendingNotification = null;
    pendingNotificationClassNamePrefix = null;
  }
};

export const dismissNotification = (classNamePrefix: string): void => {
  cancelPendingNotification(classNamePrefix);
  closeNotification(classNamePrefix);
};

// Dispatch a print request now if the listener is ready, otherwise queue it so
// clicking "Print PDF" before the list (and its ActionMenu listener) has
// mounted still works.
export const requestPrintWhenReady = (dispatch: () => void): void => {
  if (isPrintListenerReady) {
    dispatch();
  } else {
    pendingPrintDispatch = dispatch;
  }
};

export const setPrintListenerReady = (ready: boolean) => {
  isPrintListenerReady = ready;

  if (ready) {
    flushPendingOperationNotifications();

    if (pendingPrintDispatch) {
      const dispatch = pendingPrintDispatch;
      pendingPrintDispatch = null;
      dispatch();
    }
  }
};

export const createNotificationWithPrintListener = (
  type: NotificationType,
  strings: NotificationStrings,
  notificationKey: string,
  classNamePrefix: string,
  options?: NotificationOptions
) => {
  const showNotification = () => {
    createGenericNotification(
      type,
      strings,
      notificationKey,
      classNamePrefix,
      options
    );
  };

  if (type === NOTIFICATION_TYPES.CREATE_SUCCESS && !isPrintListenerReady) {
    pendingNotification = showNotification;
    pendingNotificationClassNamePrefix = classNamePrefix;
    return;
  }

  showNotification();
};
